import React, { useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { BrokerInvitationFields } from "./BrokerInvitationFields.models";
import { Button, Typography } from "@safetyculture/sc-web-ui";
import { InputWithValidation } from "../../../components/form/InputWithValidation";
import { FormRow, InviteFormGrid } from "../styles.css";
import { validation_rules } from "../../../components/form/_validation_rules";
import { Container } from "../../../components/util";
import {
  SelectOption,
  SelectWithValidation,
} from "../../../components/form/SelectWithValidation";
import { useOrgSelectOptions } from "../../../hooks/queries/select_options/useOrgSelectOptions.query";
import { useFlag } from "../../../config/featureFlags";
import { useLocation } from "react-router-dom";
import AdminService, {
  IOrganisation,
} from "../../../services/admin/admin.service";
import { useAsyncEffect } from "../../../hooks/useAsyncEffect";
import { feature_flag } from "../../../constants";

type Props = {
  onSubmit: (fields: BrokerInvitationFields) => Promise<void>;
};

export const BrokerInviteForm: React.FC<Props> = ({ onSubmit }) => {
  const location = useLocation();
  const is_org_field_enabled = useFlag(
    feature_flag.BROKER_INVITE_TO_EXISTING_ORG,
  );
  const [org_search_term, setOrgSearchTerm] = useState("");
  const to_org_id_param: string | null = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("to_org_id");
  }, [location.search]);

  const orgs = useOrgSelectOptions({
    filtered_out_org_types: ["customer"],
    org_search_term,
  });

  const form = useForm<BrokerInvitationFields>({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      invite_to_brokerage_org_id_selection: undefined,
    },
    mode: "all",
  });

  useAsyncEffect(async () => {
    if (is_org_field_enabled) {
      // behind feature flag
      if (to_org_id_param) {
        const found_org: IOrganisation | undefined =
          await AdminService.fetchOrgById(to_org_id_param);
        if (found_org) {
          const pre_selected_org: SelectOption<IOrganisation> = {
            value: found_org.id,
            label: found_org.name,
            meta: found_org,
          };
          form.setValue(
            "invite_to_brokerage_org_id_selection",
            pre_selected_org,
          );
        }
      }
    }
  }, [to_org_id_param]);

  return (
    <FormProvider {...form}>
      <Typography variant="titleLarge">Broker details</Typography>
      <Container>
        <InviteFormGrid>
          <FormRow>
            <Container className="column">
              <InputWithValidation
                name="firstname"
                rules={{
                  required: { value: true, message: "First name is required" },
                }}
                label="First name"
                placeholder="First name"
                control={form.control}
              />
            </Container>
            <Container className="column">
              <InputWithValidation
                name="lastname"
                rules={{
                  required: { value: true, message: "Last name is required" },
                }}
                label="Last name"
                placeholder="Last name"
                control={form.control}
              />
            </Container>
          </FormRow>
          <InputWithValidation
            name="email"
            rules={{
              required: { value: true, message: "Email address is required" },
              pattern: {
                value: validation_rules.patterns.email,
                message: "Invalid email address",
              },
            }}
            label="Email"
            placeholder="Email address"
            control={form.control}
            margin={{ top: 24, bottom: 24 }}
          />
          {is_org_field_enabled && (
            <SelectWithValidation
              name="invite_to_brokerage_org_id_selection"
              label="Brokerage organisation"
              placeholder="Find organisation by name or id"
              control={form.control}
              rules={{
                required: validation_rules.select.required(
                  "Brokerage organisation is required",
                ),
              }}
              margin={{ bottom: 24 }}
              data={orgs.data}
              isLoading={orgs.isLoading}
              searchable={setOrgSearchTerm}
            />
          )}
        </InviteFormGrid>

        <InviteFormGrid>
          <Button
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            style={{ marginTop: 32 }}
            variant="primary"
            isLoading={form.formState.isSubmitting}
          >
            Submit
          </Button>
        </InviteFormGrid>
      </Container>
    </FormProvider>
  );
};
